

import React from "react"

import { useState, useEffect } from "react"
import { database, auth } from "../firebase"
import { ref, onValue, off } from "firebase/database"
import { onAuthStateChanged } from "firebase/auth"
import ReactDOMServer from "react-dom/server"

// Helper: Extract city from address
const getCityFromAddress = (address) => {
  if (!address) return "Unknown City"
  const addressParts = address.trim().split(/\s+/)
  return addressParts.length > 0 ? addressParts[addressParts.length - 1] : "Unknown City"
}

// Filter Section Component
const FilterSection = ({ title, options, selected, onChange }) => {
  const handleCheckboxChange = (option) => {
    if (option === options[0]) {
      // "All" option
      if (selected.includes(option)) {
        onChange([])
      } else {
        onChange([...options])
      }
    } else {
      const newSelected = selected.includes(option)
        ? selected.filter((item) => item !== option && item !== options[0])
        : [...selected.filter((item) => item !== options[0]), option]
      if (newSelected.length === options.length - 1) {
        onChange([options[0], ...newSelected])
      } else {
        onChange(newSelected)
      }
    }
  }

  return React.createElement(
    "div",
    {
      style: {
        backgroundColor: "#f8fafc",
        padding: "16px",
        borderRadius: "8px",
        border: "1px solid #e2e8f0",
        boxShadow: "0 2px 4px rgba(0, 0, 0, 0.05)",
        transition: "all 0.3s ease",
        maxWidth: "220px",
        marginBottom: "16px",
      },
    },
    React.createElement(
      "h2",
      {
        style: {
          fontSize: "16px",
          fontWeight: "600",
          color: "#1e293b",
          marginBottom: "8px",
          borderBottom: "2px solid #3b82f6",
          paddingBottom: "6px",
        },
      },
      title,
    ),
    React.createElement(
      "div",
      { style: { display: "flex", flexDirection: "column", gap: "8px" } },
      options.map((option) =>
        React.createElement(
          "label",
          {
            key: option,
            style: {
              display: "flex",
              alignItems: "center",
              gap: "8px",
              cursor: "pointer",
              padding: "6px",
              borderRadius: "6px",
              transition: "background-color 0.2s",
              backgroundColor: selected.includes(option) ? "#eff6ff" : "transparent",
            },
            onMouseEnter: (e) => {
              if (!selected.includes(option)) {
                e.currentTarget.style.backgroundColor = "#f1f5f9"
              }
            },
            onMouseLeave: (e) => {
              if (!selected.includes(option)) {
                e.currentTarget.style.backgroundColor = "transparent"
              }
            },
          },
          React.createElement("input", {
            type: "checkbox",
            checked: selected.includes(option),
            onChange: () => handleCheckboxChange(option),
            style: {
              height: "14px",
              width: "14px",
              accentColor: "#3b82f6",
              cursor: "pointer",
            },
          }),
          React.createElement(
            "span",
            {
              style: {
                fontSize: "14px",
                color: "#475569",
                userSelect: "none",
                fontWeight: selected.includes(option) ? "500" : "400",
              },
            },
            option,
          ),
        ),
      ),
    ),
  )
}

// Sales Product Return Report HSN Display Component
const SalesProductReturnReportHSNDisplay = ({ formData, filters, branchProfile, returnData }) => {
  // Helper function to parse date
  const parseReturnDate = (dateString) => {
    if (!dateString) return null
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      const parts = dateString.split("-")
      return new Date(Number.parseInt(parts[0], 10), Number.parseInt(parts[1], 10) - 1, Number.parseInt(parts[2], 10))
    }
    if (/^\d{2}[-/]\d{2}[-/]\d{4}$/.test(dateString)) {
      const parts = dateString.split(/[-/]/)
      return new Date(Number.parseInt(parts[2], 10), Number.parseInt(parts[1], 10) - 1, Number.parseInt(parts[0], 10))
    }
    return new Date(dateString)
  }

  // Format date to DD/MM/YYYY for display
  const formatDisplayDate = (dateString) => {
    const date = parseReturnDate(dateString)
    return !date || isNaN(date.getTime())
      ? "N/A"
      : date.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        })
  }

  // Filter returnData by selected criteria
  let filteredReturnData = returnData

  // Apply date filter
  if (formData.fromDate && formData.toDate) {
    const fromDate = new Date(formData.fromDate)
    const toDate = new Date(formData.toDate)
    toDate.setHours(23, 59, 59, 999)
    filteredReturnData = filteredReturnData.filter((item) => {
      const itemDate = parseReturnDate(item.srnDate)
      return itemDate && !isNaN(itemDate.getTime()) && itemDate >= fromDate && itemDate <= toDate
    })
  }

  // Apply store filter
  if (!filters.store.includes("All Stores")) {
    filteredReturnData = filteredReturnData.filter((item) => filters.store.includes(item.store))
  }

  // Apply customer filter
  if (!filters.customer.includes("All Customers")) {
    filteredReturnData = filteredReturnData.filter((item) => filters.customer.includes(item.customer))
  }

  // Apply group filter
  if (!filters.group.includes("All Group Names")) {
    filteredReturnData = filteredReturnData.filter((item) => filters.group.includes(item.groupName))
  }

  // Apply category filter
  if (!filters.category.includes("All Categories")) {
    filteredReturnData = filteredReturnData.filter((item) => filters.category.includes(item.category))
  }

  // Apply brand filter
  if (!filters.brand.includes("All Brands")) {
    filteredReturnData = filteredReturnData.filter((item) => filters.brand.includes(item.brand))
  }

  // Apply product filter
  if (!filters.product.includes("All Products")) {
    filteredReturnData = filteredReturnData.filter((item) => filters.product.includes(item.itemDescription))
  }

  // Initialize displayData
  let displayData = []

  // Process data based on report type
 if (formData.reportType === "abstract") {
  // Group by product for abstract productwise report
  const groupedData = filteredReturnData.reduce((acc, item) => {
    const key = item.itemDescription || "Unknown Product";
    if (!acc[key]) {
      acc[key] = {
        productName: item.itemDescription || "Unknown Product",
        commodityCode: item.hsnCode || "N/A",
        invNo: item.billNo || "N/A",
        invDate: item.srnDate,
        gstSlab:
          (Number.parseFloat(item.cgstTax) || 0) + (Number.parseFloat(item.sgstTax) || 0) > 0
            ? (
                (((Number.parseFloat(item.cgstTax) || 0) + (Number.parseFloat(item.sgstTax) || 0)) /
                  (Number.parseFloat(item.taxableValue) || 1)) *
                100
              ).toFixed(1) + "%"
            : "0%",
        cgstPercent: item.cgst ? item.cgst + "%" : "0%",
        cgstAmt: 0,
        sgstPercent: item.sgst ? item.sgst + "%" : "0%",
        sgstAmt: 0,
        cstAmt: 0,
        grossAmount: 0,
        netAmount: 0,
        count: 0,
      };
    }
    acc[key].cgstAmt += Number.parseFloat(item.cgstTax) || 0;
    acc[key].sgstAmt += Number.parseFloat(item.sgstTax) || 0;
    acc[key].grossAmount += Number.parseFloat(item.grossAmount) || 0;
    acc[key].netAmount += Number.parseFloat(item.netAmount) || 0;
    acc[key].count += 1;
    return acc;
  }, {});
  displayData = Object.values(groupedData);
  displayData.sort((a, b) => (a.productName || "").localeCompare(b.productName || ""));
} else {
  // Details report
  displayData = [...filteredReturnData].map((item) => ({
    ...item,
    returnNo: item.srnNo,
    returnDate: item.srnDate,
    supplier: item.customer, // Using customer as supplier for sales returns
    productName: item.itemDescription,
    commodityCode: item.hsnCode || "N/A",
    hsnCode: item.hsnCode || "N/A",
    invNo: item.billNo || "N/A",
    invDate: item.srnDate,
    gstSlab:
      (Number.parseFloat(item.cgstTax) || 0) + (Number.parseFloat(item.sgstTax) || 0) > 0
        ? (
            (((Number.parseFloat(item.cgstTax) || 0) + (Number.parseFloat(item.sgstTax) || 0)) /
              (Number.parseFloat(item.taxableValue) || 1)) *
            100
          ).toFixed(1) + "%"
        : "0%",
    cgstPercent: item.cgst ? item.cgst + "%" : "0%",
    cgstAmt: Number.parseFloat(item.cgstTax) || 0,
    sgstPercent: item.sgst ? item.sgst + "%" : "0%",
    sgstAmt: Number.parseFloat(item.sgstTax) || 0,
    cstAmt: 0, // CST not applicable for current GST system
    qty: Number.parseFloat(item.qty) || 0,
    taxableValue: Number.parseFloat(item.taxableValue) || 0,
    igstAmt: 0,
    cessAmt: 0,
    totalTax: (Number.parseFloat(item.cgstTax) || 0) + (Number.parseFloat(item.sgstTax) || 0),
    grossAmount: Number.parseFloat(item.grossAmount) || 0,
    netValue: Number.parseFloat(item.netAmount) || 0,
  }));
  displayData.sort((a, b) => {
    return (
      (a.hsnCode || "").localeCompare(b.hsnCode || "") ||
      parseReturnDate(a.returnDate) - parseReturnDate(b.returnDate)
    );
  });
} 

  // Calculate totals
  const totalQty = displayData.reduce((sum, item) => sum + (Number.parseFloat(item.qty) || 0), 0)
  const totalTaxableValue = displayData.reduce(
    (sum, item) => sum + (Number.parseFloat(item.taxableValue || item.grossAmount) || 0),
    0,
  )
  const totalCgstAmt = displayData.reduce((sum, item) => sum + (Number.parseFloat(item.cgstAmt) || 0), 0)
  const totalSgstAmt = displayData.reduce((sum, item) => sum + (Number.parseFloat(item.sgstAmt) || 0), 0)
  const totalIgstAmt = displayData.reduce((sum, item) => sum + (Number.parseFloat(item.igstAmt) || 0), 0)
  const totalCessAmt = displayData.reduce((sum, item) => sum + (Number.parseFloat(item.cessAmt) || 0), 0)
  const totalCstAmt = displayData.reduce((sum, item) => sum + (Number.parseFloat(item.cstAmt) || 0), 0)
  const totalTax = displayData.reduce((sum, item) => sum + (Number.parseFloat(item.totalTax) || 0), 0)
  const totalNetValue = displayData.reduce(
    (sum, item) => sum + (Number.parseFloat(item.netValue || item.netAmount) || 0),
    0,
  )
  const totalGrossAmount = displayData.reduce(
    (sum, item) => sum + (Number.parseFloat(item.grossAmount || item.taxableValue) || 0),
    0,
  )

  const city = branchProfile.city || (branchProfile.address ? branchProfile.address.split(/\s+/).pop() : 'Unknown City')

  const getReportTitle = () => {
    return "Productwise Sales Annexure Return Report"
  }

  const renderTableHeaders = () => {
    if (formData.reportType === "abstract") {
      return (
        <tr style={{ backgroundColor: '#f1f5f9', borderBottom: '2px solid #3b82f6' }}>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>
            S.No
          </th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>
            Product Name
          </th>
          {/* <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>
            Commodity Code
          </th> */}
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>
            Inv.No
          </th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>
            Inv.Date
          </th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>
            GST SLAB
          </th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>
            CGST %
          </th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>
            CGST AMT
          </th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>
            SGST %
          </th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>
            SGST AMT
          </th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>
            CST AMT
          </th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>
            Gross Amount
          </th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>
            Net Amount
          </th>
        </tr>
      )
    } else {
      return (
        <tr style={{ backgroundColor: '#f1f5f9', borderBottom: '2px solid #3b82f6' }}>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>
            S.No
          </th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>
            Product Name
          </th>
          {/* <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>
            Commodity Code
          </th> */}
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>
            Inv.No
          </th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>
            Inv.Date
          </th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>
            GST SLAB
          </th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>
            CGST %
          </th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>
            CGST AMT
          </th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>
            SGST %
          </th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>
            SGST AMT
          </th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>
            CST AMT
          </th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>
            Gross Amount
          </th>
          <th style={{ border: '1px solid #cbd5e1', padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '12px' }}>
            Net Amount
          </th>
        </tr>
      )
    }
  }

  const renderTableRows = () => {
    if (displayData.length === 0) {
      const colSpan = 13
      return (
        <tr>
          <td
            colSpan={colSpan}
            style={{
              border: '1px solid #cbd5e1',
              padding: '30px',
              textAlign: 'center',
              color: '#64748b',
              fontStyle: 'italic',
              fontSize: '14px',
              backgroundColor: '#f8fafc',
            }}
          >
            No return data found for the selected criteria
          </td>
        </tr>
      )
    }

    return displayData.map((item, index) => {
      if (formData.reportType === "abstract") {
        return (
          <tr key={`${item.productName}-${index}`} style={{ backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8fafc' }}>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>
              {index + 1}
            </td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'left', fontSize: '11px' }}>
              {item.productName}
            </td>
            {/* <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>
              {item.commodityCode}
            </td> */}
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>
              {item.invNo}
            </td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>
              {formatDisplayDate(item.invDate)}
            </td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>
              {item.gstSlab}
            </td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>
              {item.cgstPercent}
            </td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>
              {item.cgstAmt.toFixed(2)}
            </td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>
              {item.sgstPercent}
            </td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>
              {item.sgstAmt.toFixed(2)}
            </td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>
              {item.cstAmt.toFixed(2)}
            </td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>
              {item.grossAmount.toFixed(2)}
            </td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>
              {item.netAmount.toFixed(2)}
            </td>
          </tr>
        )
      } else {
        return (
          <tr
            key={`${item.returnNo}-${item.productName}-${index}`}
            style={{ backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8fafc' }}
          >
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>
              {index + 1}
            </td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'left', fontSize: '11px' }}>
              {item.productName}
            </td>
            {/* <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>
              {item.commodityCode}
            </td> */}
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>
              {item.invNo}
            </td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>
              {formatDisplayDate(item.invDate)}
            </td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>
              {item.gstSlab}
            </td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>
              {item.cgstPercent}
            </td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>
              {item.cgstAmt.toFixed(2)}
            </td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '11px' }}>
              {item.sgstPercent}
            </td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>
              {item.sgstAmt.toFixed(2)}
            </td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>
              {item.cstAmt.toFixed(2)}
            </td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>
              {item.grossAmount.toFixed(2)}
            </td>
            <td style={{ border: '1px solid #cbd5e1', padding: '8px', textAlign: 'right', fontSize: '11px' }}>
              {item.netValue.toFixed(2)}
            </td>
          </tr>
        )
      }
    })
  }

  const renderTotalRow = () => {
    if (displayData.length === 0) return null

    return (
      <tr style={{ backgroundColor: '#fef3c7', fontWeight: 'bold', borderTop: '2px solid #f59e0b' }}>
        <td
          colSpan="6"
          style={{
            border: '1px solid #cbd5e1',
            padding: '10px',
            textAlign: 'center',
            fontSize: '12px',
            fontWeight: '600',
          }}
        >
          Total
        </td>
        <td
          style={{
            border: '1px solid #cbd5e1',
            padding: '10px',
            textAlign: 'right',
            fontSize: '12px',
            fontWeight: '600',
          }}
        >
          {totalCgstAmt.toFixed(2)}
        </td>
        <td
          style={{
            border: '1px solid #cbd5e1',
            padding: '10px',
            textAlign: 'center',
            fontSize: '12px',
            fontWeight: '600',
          }}
        >
        </td>
        <td
          style={{
            border: '1px solid #cbd5e1',
            padding: '10px',
            textAlign: 'right',
            fontSize: '12px',
            fontWeight: '600',
          }}
        >
          {totalSgstAmt.toFixed(2)}
        </td>
        <td
          style={{
            border: '1px solid #cbd5e1',
            padding: '10px',
            textAlign: 'right',
            fontSize: '12px',
            fontWeight: '600',
          }}
        >
          {totalCstAmt.toFixed(2)}
        </td>
        <td
          style={{
            border: '1px solid #cbd5e1',
            padding: '10px',
            textAlign: 'right',
            fontSize: '12px',
            fontWeight: '600',
          }}
        >
          {totalGrossAmount.toFixed(2)}
        </td>
        <td
          style={{
            border: '1px solid #cbd5e1',
            padding: '10px',
            textAlign: 'right',
            fontSize: '12px',
            fontWeight: '600',
          }}
        >
          {totalNetValue.toFixed(2)}
        </td>
      </tr>
    )
  }

  const renderGrandTotalRow = () => {
    if (displayData.length === 0) return null

    return (
      <tr style={{ backgroundColor: '#fee2e2', fontWeight: 'bold', borderTop: '2px solid #dc2626' }}>
        <td
          colSpan="6"
          style={{
            border: '1px solid #cbd5e1',
            padding: '10px',
            textAlign: 'center',
            fontSize: '12px',
            fontWeight: '600',
          }}
        >
          Grand Total
        </td>
        <td
          style={{
            border: '1px solid #cbd5e1',
            padding: '10px',
            textAlign: 'right',
            fontSize: '12px',
            fontWeight: '600',
          }}
        >
          {totalCgstAmt.toFixed(2)}
        </td>
        <td
          style={{
            border: '1px solid #cbd5e1',
            padding: '10px',
            textAlign: 'center',
            fontSize: '12px',
            fontWeight: '600',
          }}
        >
        </td>
        <td
          style={{
            border: '1px solid #cbd5e1',
            padding: '10px',
            textAlign: 'right',
            fontSize: '12px',
            fontWeight: '600',
          }}
        >
          {totalSgstAmt.toFixed(2)}
        </td>
        <td
          style={{
            border: '1px solid #cbd5e1',
            padding: '10px',
            textAlign: 'right',
            fontSize: '12px',
            fontWeight: '600',
          }}
        >
          {totalCstAmt.toFixed(2)}
        </td>
        <td
          style={{
            border: '1px solid #cbd5e1',
            padding: '10px',
            textAlign: 'right',
            fontSize: '12px',
            fontWeight: '600',
          }}
        >
          {totalGrossAmount.toFixed(2)}
        </td>
        <td
          style={{
            border: '1px solid #cbd5e1',
            padding: '10px',
            textAlign: 'right',
            fontSize: '12px',
            fontWeight: '600',
          }}
        >
          {totalNetValue.toFixed(2)}
        </td>
      </tr>
    )
  }

  return (
    <div
      style={{
        backgroundColor: 'white',
        padding: '30px',
        marginTop: '20px',
        border: '2px solid #3b82f6',
        borderRadius: '12px',
        fontFamily: 'Arial, sans-serif',
        fontSize: '12px',
        pageBreakInside: 'avoid',
        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
      }}
      className="report-display"
    >
      {/* Header */}
      <div
        style={{
          textAlign: 'center',
          marginBottom: '30px',
          paddingBottom: '20px',
          borderBottom: '2px solid #e2e8f0',
        }}
      >
        <h1
          style={{
            fontSize: '24px',
            fontWeight: 'bold',
            margin: '0',
            marginBottom: '5px',
            color: '#1e293b',
            textShadow: '1px 1px 2px rgba(0,0,0,0.1)',
          }}
        >
          {branchProfile.branchName}
        </h1>
        <div style={{ fontSize: '16px', fontWeight: '600', color: '#3b82f6', marginBottom: '10px' }}>{city}</div>
        <div style={{ fontSize: '14px', color: '#475569', marginBottom: '10px' }}>
          {getReportTitle()} From : 
          {formData.fromDate ? new Date(formData.fromDate).toLocaleDateString('en-GB') : 'N/A'} To : 
          {formData.toDate ? new Date(formData.toDate).toLocaleDateString('en-GB') : 'N/A'}
        </div>
        <div style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>
          Option: {formData.reportType === 'details' ? 'Detail' : 'Abstract'}
        </div>
      </div>

      {/* Returns Table */}
      <div style={{ overflowX: 'auto' }}>
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '11px',
            border: '2px solid #3b82f6',
            borderRadius: '8px',
            overflow: 'hidden',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          }}
        >
          <thead>{renderTableHeaders()}</thead>
          <tbody>
            {renderTableRows()}
            {renderTotalRow()}
            {renderGrandTotalRow()}
          </tbody>
        </table>
      </div>

      {/* Summary Section */}
      {displayData.length > 0 && (
        <div
          style={{
            marginTop: '30px',
            padding: '20px',
            backgroundColor: '#f8fafc',
            borderRadius: '8px',
            border: '1px solid #e2e8f0',
          }}
        >
          <h3
            style={{
              fontSize: '16px',
              fontWeight: '600',
              marginBottom: '15px',
              color: '#1e293b',
            }}
          >
            Summary
          </h3>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
              gap: '15px',
            }}
          >
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Total Records</div>
              <div style={{ fontSize: '18px', fontWeight: '600', color: '#3b82f6' }}>{displayData.length}</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Total CGST</div>
              <div style={{ fontSize: '18px', fontWeight: '600', color: '#059669' }}>{totalCgstAmt.toFixed(2)}</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Total SGST</div>
              <div style={{ fontSize: '18px', fontWeight: '600', color: '#dc2626' }}>{totalSgstAmt.toFixed(2)}</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Gross Amount</div>
              <div style={{ fontSize: '18px', fontWeight: '600', color: '#7c3aed' }}>{totalGrossAmount.toFixed(2)}</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Net Amount</div>
              <div style={{ fontSize: '18px', fontWeight: '600', color: '#7c3aed' }}>{totalNetValue.toFixed(2)}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// Main Component
const SalesProductReturnReportHSN_1A = () => {
  const today = new Date()
  const [fromDate, setFromDate] = useState(today.toISOString().split("T")[0])
  const [toDate, setToDate] = useState(today.toISOString().split("T")[0])
  const [searchBy, setSearchBy] = useState("productwise") // Default to productwise only
  const [reportType, setReportType] = useState("details")
  const [filters, setFilters] = useState({
    store: ["All Stores"],
    customer: ["All Customers"],
    group: ["All Group Names"],
    category: ["All Categories"],
    brand: ["All Brands"],
    product: ["All Products"],
  })
  const [stores, setStores] = useState(["All Stores"])
  const [customers, setCustomers] = useState(["All Customers"])
  const [groups, setGroups] = useState(["All Group Names"])
  const [categories, setCategories] = useState(["All Categories"])
  const [brands, setBrands] = useState(["All Brands"])
  const [products, setProducts] = useState(["All Products"])
  const [currentUser, setCurrentUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [showReport, setShowReport] = useState(false)
  const [branchProfile, setBranchProfile] = useState({
    branchName: "SUPREME TRADERS",
    address: "",
    phone: "",
    city: "",
  })
  const [returnData, setReturnData] = useState([])
  const [productGroupMap, setProductGroupMap] = useState({})
  const [productCategoryMap, setProductCategoryMap] = useState({})
  const [productBrandMap, setProductBrandMap] = useState({})

  const encodeEmailForPath = (email) => {
    return email.replace(/[.#$[\]]/g, "_")
  }

  // Firebase Auth and Data Loading
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user)
      if (user) {
        loadFirebaseData(user)
      }
      setLoading(false)
    })
    return () => unsubscribe()
  }, [])

  const loadFirebaseData = (user) => {
    const encodedEmail = encodeEmailForPath(user.email)
    // Load Branch Profile
    const branchProfileRef = ref(database, `users/${encodedEmail}/branchProfiles`)
    onValue(branchProfileRef, (snapshot) => {
      const data = snapshot.val()
      if (data) {
        const profiles = Object.values(data)
        const latestProfile = profiles[profiles.length - 1]
        setBranchProfile({
          branchName: latestProfile.branchName || "SUPREME TRADERS",
          address: `${latestProfile.address1 || ""} ${latestProfile.address2 || ""}`.trim(),
          phone: latestProfile.mobileNo || "",
          city:
            latestProfile.city || getCityFromAddress(`${latestProfile.address1 || ""} ${latestProfile.address2 || ""}`),
        })
      }
    })

    // Load Stores
    const storesRef = ref(database, `users/${encodedEmail}/stores`)
    onValue(storesRef, (snapshot) => {
      const storesData = ["All Stores"]
      if (snapshot.exists()) {
        snapshot.forEach((childSnapshot) => {
          const store = childSnapshot.val()
          if (store.name && !storesData.includes(store.name)) {
            storesData.push(store.name)
          }
        })
      }
      setStores(storesData)
    })

    // Load Customers
    const customersRef = ref(database, `users/${encodedEmail}/customers`)
    onValue(customersRef, (snapshot) => {
      const customersData = ["All Customers"]
      if (snapshot.exists()) {
        snapshot.forEach((childSnapshot) => {
          const customer = childSnapshot.val()
          if (customer.customerName && !customersData.includes(customer.customerName)) {
            customersData.push(customer.customerName)
          }
        })
      }
      setCustomers(customersData)
    })

    // Load Groups
    const groupsRef = ref(database, `users/${encodedEmail}/productGroups`)
    onValue(groupsRef, (snapshot) => {
      const groupsData = ["All Group Names"]
      if (snapshot.exists()) {
        snapshot.forEach((childSnapshot) => {
          const group = childSnapshot.val()
          if (group.name && !groupsData.includes(group.name)) {
            groupsData.push(group.name)
          }
        })
      }
      setGroups(groupsData)
    })

    // Load Categories
    const categoriesRef = ref(database, `users/${encodedEmail}/categories`)
    onValue(categoriesRef, (snapshot) => {
      const categoriesData = ["All Categories"]
      if (snapshot.exists()) {
        snapshot.forEach((childSnapshot) => {
          const category = childSnapshot.val()
          if (category.name && !categoriesData.includes(category.name)) {
            categoriesData.push(category.name)
          }
        })
      }
      setCategories(categoriesData)
    })

    // Load Brands
    const brandsRef = ref(database, `users/${encodedEmail}/brands`)
    onValue(brandsRef, (snapshot) => {
      const brandsData = ["All Brands"]
      if (snapshot.exists()) {
        snapshot.forEach((childSnapshot) => {
          const brand = childSnapshot.val()
          if (brand.name && !brandsData.includes(brand.name)) {
            brandsData.push(brand.name)
          }
        })
      }
      setBrands(brandsData)
    })

    // Load Products and create mappings
    const productsRef = ref(database, `users/${encodedEmail}/products`)
    onValue(productsRef, (snapshot) => {
      const productsData = ["All Products"]
      const groupMap = {}
      const categoryMap = {}
      const brandMap = {}
      if (snapshot.exists()) {
        snapshot.forEach((childSnapshot) => {
          const product = childSnapshot.val()
          if (product.productName && !productsData.includes(product.productName)) {
            productsData.push(product.productName)
            groupMap[product.productName] = product.groupName || product.group || "No Group Assigned"
            categoryMap[product.productName] = product.category || "Unknown Category"
            brandMap[product.productName] = product.brand || "Unknown Brand"
          }
        })
      }
      setProducts(productsData)
      setProductGroupMap(groupMap)
      setProductCategoryMap(categoryMap)
      setProductBrandMap(brandMap)
    })

  const salesReturnsRef = ref(database, `users/${encodedEmail}/salesReturns`);
onValue(salesReturnsRef, (snapshot) => {
  const data = snapshot.val();
  if (data) {
    const returnRecords = [];
    Object.entries(data).forEach(([returnId, returnData]) => {
      if (returnData.items && Array.isArray(returnData.items)) {
        returnData.items.forEach((item) => {
          if (item.itemDescription) {
            const returnQty = Number.parseFloat(item.returnQty) || 0;
            const mrp = Number.parseFloat(item.mrp) || 0;
            const discPercent = Number.parseFloat(item.disc) || 0;
            const cgstRate = Number.parseFloat(item.cgst) || 0;
            const sgstRate = Number.parseFloat(item.sgst) || 0;
            const grossAmount = returnQty * mrp;
            const discount = (grossAmount * discPercent) / 100;
            const taxableValue = grossAmount - discount;
            const cgstTax = (taxableValue * cgstRate) / 100;
            const sgstTax = (taxableValue * sgstRate) / 100;
            const netAmount = taxableValue + cgstTax + sgstTax;

            returnRecords.push({
              returnId,
              srnNo: returnData.srnNo || "N/A",
              srnDate: returnData.srnDate || "",
              billNo: returnData.billNo || "N/A",
              store: returnData.store || "Unknown Store",
              customer: returnData.customer || "Unknown Customer",
              itemDescription: item.itemDescription || "Unknown Product",
              groupName: productGroupMap[item.itemDescription] || "No Group Assigned",
              category: productCategoryMap[item.itemDescription] || "Unknown Category",
              brand: productBrandMap[item.itemDescription] || "Unknown Brand",
              barCode: item.barCode || "",
              hsnCode: item.hsnCode || "",
              uom: item.uom || "",
              qty: returnQty,
              mrp,
              grossAmount,
              discount,
              taxableValue,
              cgst: cgstRate,
              sgst: sgstRate,
              cgstTax,
              sgstTax,
              netAmount,
            });
          }
        });
      }
    });
    setReturnData(returnRecords);
  } else {
    setReturnData([]);
  }
});
    return () => {
      off(branchProfileRef)
      off(storesRef)
      off(customersRef)
      off(groupsRef)
      off(categoriesRef)
      off(brandsRef)
      off(productsRef)
      off(salesReturnsRef)
    }
  }

  // Update return data when product mappings change
  useEffect(() => {
    if (Object.keys(productGroupMap).length > 0 && currentUser) {
      setReturnData((prevData) =>
        prevData.map((item) => ({
          ...item,
          groupName: productGroupMap[item.itemDescription] || "No Group Assigned",
          category: productCategoryMap[item.itemDescription] || "Unknown Category",
          brand: productBrandMap[item.itemDescription] || "Unknown Brand",
        })),
      )
    }
  }, [productGroupMap, productCategoryMap, productBrandMap, currentUser])

  const handleFilterChange = (key, value) => {
    setFilters({ ...filters, [key]: value })
  }

  const handleGenerateReport = () => {
    if (!reportType) {
      alert("Please select a report type.")
      return
    }
    setShowReport(true)
  }

  const handlePrint = () => {
    const printContent = (
      <SalesProductReturnReportHSNDisplay
        formData={{ fromDate, toDate, searchBy, reportType }}
        filters={filters}
        branchProfile={branchProfile}
        returnData={returnData}
      />
    );
  
    const htmlString = ReactDOMServer.renderToString(printContent);
  
    const printWindow = window.open('', '', 'width=1200,height=800');
    printWindow.document.write(`
      <html>
        <head>
          <title>Sales Product Return HSN Report</title>
          <style>
            body { 
              margin: 0; 
              padding: 5px; 
              font-family: Arial, sans-serif; 
              font-size: 8px;
            }
            @page { 
              size: A4 landscape; 
              margin: 5mm; 
            }
            table { 
              page-break-inside: auto;
              width: 100%;
              font-size: 7px;
              table-layout: fixed;
              border-collapse: collapse;
            }
            tr { 
              page-break-inside: avoid; 
              page-break-after: auto; 
            }
            th, td {
              padding: 2px !important;
              font-size: 7px !important;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
              max-width: 60px;
            }
            /* Specific column widths */
            th:nth-child(1), td:nth-child(1) { width: 30px; } /* S.No */
            th:nth-child(2), td:nth-child(2) { width: 80px; } /* Product Name */
            th:nth-child(3), td:nth-child(3) { width: 50px; } /* Inv.No */
            th:nth-child(4), td:nth-child(4) { width: 50px; } /* Inv.Date */
            th:nth-child(5), td:nth-child(5) { width: 50px; } /* GST SLAB */
            th:nth-child(6), td:nth-child(6) { width: 50px; } /* CGST % */
            th:nth-child(7), td:nth-child(7) { width: 50px; } /* CGST AMT */
            th:nth-child(8), td:nth-child(8) { width: 50px; } /* SGST % */
            th:nth-child(9), td:nth-child(9) { width: 50px; } /* SGST AMT */
            th:nth-child(10), td:nth-child(10) { width: 50px; } /* CST AMT */
            th:nth-child(11), td:nth-child(11) { width: 50px; } /* Gross Amount */
            th:nth-child(12), td:nth-child(12) { width: 60px; font-weight: bold; } /* Net Amount */
            .report-display {
              width: 95% !important;
              max-width: none !important;
              overflow: visible !important;
              border: 2px solid #3b82f6 !important;
              padding: 15px !important;
            }
            div[style*="overflowX"] {
              overflow: visible !important;
            }
          </style>
        </head>
        <body>
          <div id="print-content">${htmlString}</div>
          <script>
            setTimeout(function() {
              window.print();
              window.close();
            }, 1000);
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const formData = {
    fromDate,
    toDate,
    searchBy,
    reportType,
  }

  if (loading) {
    return React.createElement(
      "div",
      {
        style: {
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "400px",
          fontSize: "18px",
          color: "#64748b",
          backgroundColor: "#f8fafc",
          borderRadius: "12px",
          border: "1px solid #e2e8f0",
        },
      },
      React.createElement(
        "div",
        { style: { textAlign: "center" } },
        React.createElement("div", {
          style: {
            width: "40px",
            height: "40px",
            border: "4px solid #e2e8f0",
            borderTop: "4px solid #3b82f6",
            borderRadius: "50%",
            animation: "spin 1s linear infinite",
            margin: "0 auto 16px",
          },
        }),
        React.createElement("div", null, "Loading Return Data..."),
      ),
    )
  }

  if (!currentUser) {
    return React.createElement(
      "div",
      {
        style: {
          textAlign: "center",
          padding: "3rem",
          color: "#64748b",
          fontSize: "18px",
          backgroundColor: "#f8fafc",
          borderRadius: "12px",
          border: "1px solid #e2e8f0",
        },
      },
      React.createElement("h2", { style: { color: "#1e293b", marginBottom: "16px" } }, "Authentication Required"),
      React.createElement("p", null, "Please login to access the Productwise Return (HSN)_1A"),
    )
  }

  return React.createElement(
    "div",
    {
      style: {
        width: "100%",
        maxWidth: "90rem",
        backgroundColor: "white",
        borderRadius: "16px",
        boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
        padding: "32px",
        margin: "0 auto",
        fontFamily: "system-ui, -apple-system, sans-serif",
        border: "1px solid #e2e8f0",
      },
    },
    React.createElement(
      "style",
      null,
      `
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
      
      @media print {
        body * {
          visibility: hidden;
        }
        .report-display, .report-display * {
          visibility: visible;
        }
        .report-display {
          position: absolute;
          left: 0;
          top: 0;
          width: 100%;
          margin: 0;
          padding: 20px;
          box-shadow: none;
          border: none;
        }
      }
    `,
    ),
    // Header
    React.createElement(
      "div",
      {
        style: {
          display: "flex",
          alignItems: "center",
          gap: "12px",
          marginBottom: "24px",
          paddingBottom: "16px",
          borderBottom: "2px solid #e2e8f0",
        },
      },
      React.createElement("div", {
        style: {
          width: "6px",
          height: "32px",
          backgroundColor: "#3b82f6",
          borderRadius: "3px",
        },
      }),
      React.createElement(
        "h1",
        {
          style: {
            fontSize: "28px",
            fontWeight: "700",
            color: "#1e293b",
            margin: "0",
          },
        },
        "Productwise Return (HSN)_1A",
      ),
    ),
    // Date Range and Search Filters
    React.createElement(
      "div",
      {
        style: {
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          gap: "20px",
          marginBottom: "32px",
          padding: "24px",
          backgroundColor: "#f8fafc",
          borderRadius: "12px",
          border: "1px solid #e2e8f0",
        },
      },
      React.createElement(
        "div",
        null,
        React.createElement(
          "label",
          {
            style: {
              display: "block",
              fontSize: "14px",
              fontWeight: "600",
              color: "#374151",
              marginBottom: "8px",
            },
          },
          "From Date",
        ),
        React.createElement("input", {
          type: "date",
          value: fromDate,
          onChange: (e) => setFromDate(e.target.value),
          style: {
            width: "100%",
            padding: "12px 16px",
            border: "1px solid #d1d5db",
            borderRadius: "8px",
            fontSize: "14px",
            outline: "none",
            transition: "all 0.2s",
            backgroundColor: "white",
            boxSizing: "border-box",
          },
          onFocus: (e) => (e.target.style.borderColor = "#3b82f6"),
          onBlur: (e) => (e.target.style.borderColor = "#d1d5db"),
        }),
      ),
      React.createElement(
        "div",
        null,
        React.createElement(
          "label",
          {
            style: {
              display: "block",
              fontSize: "14px",
              fontWeight: "600",
              color: "#374151",
              marginBottom: "8px",
            },
          },
          "To Date",
        ),
        React.createElement("input", {
          type: "date",
          value: toDate,
          onChange: (e) => setToDate(e.target.value),
          style: {
            width: "100%",
            padding: "12px 16px",
            border: "1px solid #d1d5db",
            borderRadius: "8px",
            fontSize: "14px",
            outline: "none",
            transition: "all 0.2s",
            backgroundColor: "white",
            boxSizing: "border-box",
          },
          onFocus: (e) => (e.target.style.borderColor = "#3b82f6"),
          onBlur: (e) => (e.target.style.borderColor = "#d1d5db"),
        }),
      ),
      React.createElement(
        "div",
        null,
        React.createElement(
          "label",
          {
            style: {
              display: "block",
              fontSize: "14px",
              fontWeight: "600",
              color: "#374151",
              marginBottom: "8px",
            },
          },
          "Report Type",
        ),
        React.createElement(
          "select",
          {
            value: reportType,
            onChange: (e) => setReportType(e.target.value),
            style: {
              width: "100%",
              padding: "12px 16px",
              border: "1px solid #d1d5db",
              borderRadius: "8px",
              fontSize: "14px",
              outline: "none",
              transition: "all 0.2s",
              backgroundColor: "white",
              cursor: "pointer",
              boxSizing: "border-box",
            },
            onFocus: (e) => (e.target.style.borderColor = "#3b82f6"),
            onBlur: (e) => (e.target.style.borderColor = "#d1d5db"),
          },
          React.createElement("option", { value: "details" }, "Details"),
          React.createElement("option", { value: "abstract" }, "Abstract"),
        ),
      ),
    ),
    // Filter Checkboxes
    React.createElement(
      "div",
      {
        style: {
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: "20px",
          marginBottom: "32px",
        },
      },
      // React.createElement(FilterSection, {
      //   title: "Stores",
      //   options: stores,
      //   selected: filters.store,
      //   onChange: (value) => handleFilterChange("store", value),
      // }),
      React.createElement(FilterSection, {
        title: "Customer",
        options: customers,
        selected: filters.customer,
        onChange: (value) => handleFilterChange("customer", value),
      }),
      React.createElement(FilterSection, {
        title: "Group Name",
        options: groups,
        selected: filters.group,
        onChange: (value) => handleFilterChange("group", value),
      }),
      React.createElement(FilterSection, {
        title: "Category",
        options: categories,
        selected: filters.category,
        onChange: (value) => handleFilterChange("category", value),
      }),
      React.createElement(FilterSection, {
        title: "Brand",
        options: brands,
        selected: filters.brand,
        onChange: (value) => handleFilterChange("brand", value),
      }),
      React.createElement(FilterSection, {
        title: "Product",
        options: products,
        selected: filters.product,
        onChange: (value) => handleFilterChange("product", value),
      }),
    ),
    // Action Buttons
    React.createElement(
      "div",
      {
        style: {
          display: "flex",
          gap: "16px",
          marginBottom: "32px",
          justifyContent: "center",
        },
      },
      React.createElement(
        "button",
        {
          onClick: handleGenerateReport,
          style: {
            background: "linear-gradient(135deg, #16a34a 0%, #15803d 100%)",
            color: "white",
            padding: "12px 24px",
            borderRadius: "8px",
            border: "none",
            cursor: "pointer",
            transition: "all 0.3s",
            fontSize: "16px",
            fontWeight: "600",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            minWidth: "140px",
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
          },
          onMouseEnter: (e) => {
            e.currentTarget.style.transform = "translateY(-2px)"
            e.currentTarget.style.boxShadow = "0 6px 12px rgba(0, 0, 0, 0.15)"
          },
          onMouseLeave: (e) => {
            e.currentTarget.style.transform = "translateY(0)"
            e.currentTarget.style.boxShadow = "0 4px 6px rgba(0, 0, 0, 0.1)"
          },
        },
        "Generate Report",
      ),
      React.createElement(
        "button",
        {
          onClick: handlePrint,
          style: {
            background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
            color: "white",
            padding: "12px 24px",
            borderRadius: "8px",
            border: "none",
            cursor: "pointer",
            transition: "all 0.3s",
            fontSize: "16px",
            fontWeight: "600",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            minWidth: "120px",
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
          },
          onMouseEnter: (e) => {
            e.currentTarget.style.transform = "translateY(-2px)"
            e.currentTarget.style.boxShadow = "0 6px 12px rgba(0, 0, 0, 0.15)"
          },
          onMouseLeave: (e) => {
            e.currentTarget.style.transform = "translateY(0)"
            e.currentTarget.style.boxShadow = "0 4px 6px rgba(0, 0, 0, 0.1)"
          },
        },
        "Print Report",
      ),
    ),
    // Report Display
    showReport &&
      React.createElement(SalesProductReturnReportHSNDisplay, {
        formData: formData,
        filters: filters,
        branchProfile: branchProfile,
        returnData: returnData,
      }),
  )
}

export default SalesProductReturnReportHSN_1A
